import { useEffect, useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePartnerConnectStatus } from '@/hooks/usePartnerConnectStatus';
import { partnerConnectPresentation } from '@/lib/partnerConnectPresentation';
import { payoutLocale } from '@/lib/sitePayouts';
import { payoutSetupCopy } from '@/lib/payoutSetupCopy';
import { agentPayoutStatusLabel, type PayoutStatusTone } from '@/lib/agentPayoutStatus';
import {
  BarChart3, DollarSign, Battery, ListChecks, Wallet, Activity,
  CheckCircle2, AlertCircle,
} from 'lucide-react';
import {
  listAgents, listAgentPayouts, fetchOperatorSummary, fetchSessions,
  fetchChargerUtilization, fetchChargerUptime, fetchSessionTrends,
  fetchLocations, fetchAgentDashboardLink, startAgentConnect,
  formatCents, getDisplayErrorMessage,
  type AgentRow, type Location, type Session,
} from '@/lib/api';
import { downloadAuthenticatedCsv } from '@/lib/downloadAuthenticatedCsv';
import { downloadErrorMessage } from '@/lib/downloadAuthed';
import {
  commissionAxis, commissionChartFooter, commissionCharts, commissionCurrencies, commissionTotals,
  formatCommissionAverage, formatCommissionRow, formatCommissionTotals, type CommissionRow,
} from '@/lib/spCommissionMoney';
import { KpiCard, RevenueChart, RevenuePeriodPill, ThroughputChart, ExportActions } from '@evi/ui';
import type { OperatorDashboardPeriod } from '@evi/shared';
import { useAuthStore } from '@/store/auth';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { SalesPartnerConnectBanner } from '@/components/sp/SalesPartnerConnectBanner';
import {
  ScopedReportsPage,
  type PeriodOption,
  type TabSpec,
} from '@/components/reports/ScopedReportsPage';
import styles from './SalesPartnerReportsPage.module.css';

/**
 * Phase 7g.4b.3 Commit 4b , Sales Partner Reports page.
 *
 * Headline surface for SP self-service reporting. Mirrors the
 * operator ReportsPage architecture (single-file with co-located
 * tab bodies, URL-driven state, period chip filter, location
 * filter dropdown) but ships a 5-tab inventory tuned for an SP
 * audience instead of the operator's 8.
 *
 * Tab inventory:
 *   1. Commission , replaces Revenue framing for SP. Pulls
 *      listAgentPayouts and derives commission-this-period,
 *      commission-lifetime, session-count, avg-per-session.
 *   2. Sessions , mirrors operator Sessions. Server-scoped to SP
 *      chargers via Phase 7g.4a. Includes inline CSV export.
 *   3. Energy , mirrors operator Energy. operator-summary's
 *      energyLast24h series, SP-scoped via dashboardScope's
 *      sales_agent branch.
 *   4. Utilization , mirrors operator Utilization. Three SP-scoped
 *      endpoints (utilization rows, uptime, session trends).
 *   5. Payouts , net-new composition for SP. Stripe Connect
 *      status strip at top of the tab body, AgentPayouts ledger
 *      below, CSV export via the new admin/agents/:id/payouts/
 *      export.csv endpoint.
 *
 * Excluded operator tabs: Uptime (folded into Utilization KPIs),
 * Fleet Billing (SPs don't own fleet contracts), Customer Groups
 * (operator-side surface). The 5-tab shape is the deliberate SP
 * scope.
 *
 * Banner mounts: SalesPartnerConnectBanner (extracted in Commit
 * 4a) renders above the tab bar so the persistent Connect
 * enforcement follows the SP across surfaces. The wallet-
 * attribution disclosure banner that previously mounted here
 * was retired in Phase 7g.4c. Reports uses inline-flow banners
 * rather than the Dashboard's position:fixed pattern. The Dashboard has a satellite map
 * background that forces fixed banners to escape the overlay;
 * Reports is a normal scrolling content surface so inline flow
 * keeps the layout simpler. No callback-driven top-padding math
 * needed here.
 *
 * Future refactor: when a third Reports consumer ships (Building
 * Admin or Property Manager), extract operator + SP + new persona
 * into a single <ScopedReportsPage> shell. Filed at
 * docs/tickets/scoped-reports-shell.md. YAGNI dominates at two
 * consumers today.
 */

// ── Tab catalogue ───────────────────────────────────────────────────

type SpReportTab = 'commission' | 'sessions' | 'energy' | 'utilization' | 'payouts';

/** SP-local tab catalogue entry. Mapped to the shell's TabSpec via
 *  the spTabs closure in SalesPartnerReportsPage below. */
interface SpTabCatalogueEntry {
  id: SpReportTab;
  label: string;
  icon: typeof BarChart3;
}

const TABS: readonly SpTabCatalogueEntry[] = [
  { id: 'commission',  label: 'Commission',  icon: DollarSign },
  { id: 'sessions',    label: 'Sessions',    icon: ListChecks },
  { id: 'energy',      label: 'Energy',      icon: Battery    },
  { id: 'utilization', label: 'Utilization', icon: Activity   },
  { id: 'payouts',     label: 'Payouts',     icon: Wallet     },
] as const;

/**
 * COMMISSIONS IS ITS OWN SURFACE NOW (Warren's ruling, 2026-09-24).
 *
 * It was the first of the five tabs above, so the only way to reach an
 * SP's commission was to open Reports and land on it by default. That
 * is the number they open the admin for, so it gets its own nav leaf
 * and its own address at /commissions.
 *
 * Both surfaces are the same component with a different tab slice ,
 * they share the agent fetch, the locations fetch, the period chips,
 * the location filter and the Connect banner, so the promoted page
 * cannot drift from the report it came out of. ScopedReportsPage
 * suppresses its tab bar for a single-tab surface, so /commissions
 * reads as a page rather than a report with one lonely tab.
 */
const COMMISSION_TABS = TABS.filter((t) => t.id === 'commission');
const REPORT_TABS = TABS.filter((t) => t.id !== 'commission');

// VALID_TABS / isSpReportTab removed in the ScopedReportsPage
// extraction , the shell handles URL state validation via the
// isValidTabId predicate it derives from the tabs[] array we pass.

// ── Period filter ───────────────────────────────────────────────────

type ReportPeriod = OperatorDashboardPeriod | '90d' | 'custom';

const PERIOD_OPTIONS: ReadonlyArray<{ id: ReportPeriod; label: string; supported: boolean }> = [
  { id: '24h',    label: '24h',    supported: true },
  { id: '7d',     label: '7d',     supported: true },
  { id: '30d',    label: '30d',    supported: true },
  { id: '90d',    label: '90d',    supported: false },
  { id: 'custom', label: 'Custom', supported: false },
];

const VALID_PERIODS = new Set<ReportPeriod>(PERIOD_OPTIONS.map((p) => p.id));
function isReportPeriod(v: string): v is ReportPeriod {
  return VALID_PERIODS.has(v as ReportPeriod);
}

function periodForApi(p: ReportPeriod): OperatorDashboardPeriod {
  return p === '24h' || p === '7d' || p === '30d' ? p : '7d';
}

function periodToFromTo(period: ReportPeriod): { from?: string; to?: string } {
  if (period === '24h' || period === '7d' || period === '30d') {
    const days = period === '24h' ? 1 : period === '7d' ? 7 : 30;
    const to = new Date();
    const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  }
  return {};
}

function describePeriod(p: ReportPeriod): string {
  switch (p) {
    case '24h': return 'last 24 hours';
    case '7d':  return 'last 7 days';
    case '30d': return 'last 30 days';
    case '90d': return 'last 90 days';
    case 'custom': return 'custom range';
  }
}

// ── URL state hook ──────────────────────────────────────────────────
//
// Same lightweight pattern operator ReportsPage uses. Reads
// searchParams from window.location, writes via history.replaceState
// (no back-button noise per filter tick). Listens to popstate so
// external nav (back/forward) is honored. Local to the file rather
// than extracted to a shared hook because the operator + SP variants
// are still small enough that DRY costs more than it gains. Filed
// follow-up for an extracted ScopedReportsPage shell when a third
// consumer lands.

// URL state hook (ReportsUrlState / readUrlState / useReportsUrlState)
// removed in the ScopedReportsPage extraction (commit 3 of Building
// Admin Reports). The shared shell at apps/admin/src/components/
// reports/ScopedReportsPage.tsx now owns the hook, parameterised on
// the default-tab + valid-id predicates each consumer derives from
// its TABS / PERIOD_OPTIONS arrays.

// ── Pending statuses (commission KPI grouping). Mirror of the same
//    set in SalesPartnerDashboardPage so the two surfaces classify
//    pending money identically. ────────────────────────────────────
const PENDING_STATUSES = new Set<string>(['pending', 'failed', 'pending_stripe_setup']);

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// ── SalesPartnerReportsPage ─────────────────────────────────────────

const PERIOD_OPTIONS_FOR_SHELL: ReadonlyArray<PeriodOption> = PERIOD_OPTIONS;

/** What distinguishes the two SP report surfaces. Everything else ,
 *  the queries, the filters, the banner , is shared. */
const SURFACES = {
  reports: {
    recentLabel: 'Reports',
    title: 'Reports',
    subtitle: 'Your sessions, energy, and payout history.',
    tabs: REPORT_TABS,
    defaultTabId: 'sessions',
  },
  commissions: {
    recentLabel: 'Commissions',
    title: 'Commissions',
    subtitle: 'What you have earned, and on which chargers.',
    tabs: COMMISSION_TABS,
    defaultTabId: 'commission',
  },
} as const;

export function SalesPartnerReportsPage() {
  return <SpReportsSurface variant="reports" />;
}

/** /commissions , the promoted first tab. */
export function SalesPartnerCommissionsPage() {
  return <SpReportsSurface variant="commissions" />;
}

function SpReportsSurface({ variant }: { variant: keyof typeof SURFACES }) {
  const surface = SURFACES[variant];
  useRecentRoute(surface.recentLabel);
  const user = useAuthStore((s) => s.user);
  const agentId = user?.agent_id ?? null;

  // Agent fetch , drives the Connect banner + the Payouts tab status
  // strip. listAgents is SP-scoped per Phase 7g.4a (returns array-of-
  // one for SP callers). Same pattern as SP Dashboard.
  const agentsQ = useQuery({
    queryKey: ['sp-reports', 'agents', agentId],
    queryFn: listAgents,
    enabled: !!agentId,
    staleTime: 30_000,
  });
  const agent: AgentRow | null = useMemo(() => {
    if (!agentsQ.data || !agentId) return null;
    return agentsQ.data.find((a) => a.id === agentId) ?? null;
  }, [agentsQ.data, agentId]);

  // Locations list , drives the filter dropdown. Server-scoped to SP
  // (via Phase 7g.4a sales_agent branch in dashboardScope), so this
  // returns only locations where the SP has assigned chargers.
  const locationsQ = useQuery({
    queryKey: ['sp-reports', 'locations'],
    queryFn: () => fetchLocations({ limit: 500 }),
    staleTime: 5 * 60_000,
  });
  const locations = locationsQ.data?.data ?? [];

  // Build the SP tab inventory as TabSpec[]. Each render closure
  // captures agentId / agent / locations as needed. Period is
  // coerced through isReportPeriod since the shell hands back a
  // `string` (persona-agnostic); defensive fallback to '7d' matches
  // the prior URL validator behaviour.
  const spTabs: ReadonlyArray<TabSpec> = useMemo(() => {
    // agentId is non-null here at render time because the no-agent
    // guard below returns early before the shell mounts. Pinning the
    // value in a const lets the closures capture it as non-null.
    const safeAgentId = agentId ?? '';
    return surface.tabs.map((tabSpec) => ({
      id: tabSpec.id,
      label: tabSpec.label,
      icon: tabSpec.icon,
      render: ({ period, locationId, onPeriodChange }) => {
        const reportPeriod: ReportPeriod = isReportPeriod(period) ? period : '7d';
        if (tabSpec.id === 'commission') {
          return <CommissionTab
            agentId={safeAgentId}
            period={reportPeriod}
            locationId={locationId}
            payoutCountry={agent?.payout_country ?? null}
          />;
        }
        if (tabSpec.id === 'sessions') {
          return <SessionsTab
            period={reportPeriod}
            locationId={locationId}
            locations={locations}
          />;
        }
        if (tabSpec.id === 'energy') {
          return <EnergyTab
            period={reportPeriod}
            locationId={locationId}
            onPeriodChange={(next) => onPeriodChange(next)}
          />;
        }
        if (tabSpec.id === 'utilization') {
          return <UtilizationTab locationId={locationId} />;
        }
        // payouts
        return <PayoutsTab agentId={safeAgentId} agent={agent} />;
      },
    }));
  }, [agentId, agent, locations, surface.tabs]);

  // No-agent guard. Defensive: SPs land here via App.tsx's SP-
  // conditional render branch which keys on user.is_sales_agent.
  // If is_sales_agent is true but agent_id is missing (data drift),
  // surface a clear message rather than firing queries that 401.
  // Renders the page header + error banner WITHOUT the shell ,
  // there's no useful filtered/tabbed surface to show.
  if (!agentId) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>{surface.title}</h1>
          <p className={styles.subtitle}>{surface.subtitle}</p>
        </header>
        <div className={styles.errorBanner}>
          <AlertCircle size={16} />
          <span>
            Your Sales Partner profile is not fully set up.
            Contact your administrator.
          </span>
        </div>
      </div>
    );
  }

  return (
    <ScopedReportsPage
      title={surface.title}
      headerActions={variant === 'commissions' && agentId && <StripeConnectStatusStrip agentId={agentId} agent={agent} />}
      subtitle={surface.subtitle}
      tabs={spTabs}
      defaultTabId={surface.defaultTabId}
      periodOptions={PERIOD_OPTIONS_FOR_SHELL}
      defaultPeriodId="7d"
      locations={locations}
      locationFilter="always"
      chromeOrder="filter-first"
      // Reports offers the quiet early-setup row; Commissions shows no setup banner until money is owed
      // (SPEC_PAYOUT_SETUP_WHEN_MONEY; Warren, 28 October 2026).
      bannerStack={<SalesPartnerConnectBanner agent={agent} agentId={agentId} early={variant === 'reports'} />}
    />
  );
}

// ── Commission tab body ────────────────────────────────────────────
//
// Source: listAgentPayouts(agentId). Server returns rows scoped to
// the SP (Phase 7g.4a's resolveLinkedAgentId guard). Client buckets
// by period for the chart and derives the 4 KPI tiles.
//
// Period bucketing:
//   - 24h: 24 hourly buckets
//   - 7d:  7 daily buckets
//   - 30d: 30 daily buckets
// Older payouts roll out of the visible window but stay in lifetime
// totals. Bucketing happens client-side because the server endpoint
// doesn't aggregate; row count stays bounded by the soft cap of 200
// per Phase 7e.

function CommissionTab({
  agentId, period, locationId, payoutCountry,
}: {
  agentId: string;
  period: ReportPeriod;
  locationId: string | null;
  payoutCountry: string | null;
}) {
  const supportedPeriod = period === '24h' || period === '7d' || period === '30d';

  const payoutsQ = useQuery({
    queryKey: ['sp-reports', 'payouts', agentId],
    queryFn: () => listAgentPayouts(agentId),
    enabled: supportedPeriod,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  const allPayouts = useMemo<CommissionRow[]>(
    () => payoutsQ.data?.rows ?? [],
    [payoutsQ.data],
  );

  // Narrow by location filter when set. AgentPayout rows carry
  // location_id post-Commit 2 (hydrated from the Charger doc).
  const payouts = useMemo(() => {
    if (!locationId) return allPayouts;
    return allPayouts.filter((p) => p.location_id === locationId);
  }, [allPayouts, locationId]);

  // The currencies this card is shown in: the rows' own, else the payout
  // account's. Every figure below is one per currency, never a sum across
  // two (Warren, 2026-10-16).
  const currencies = useMemo(
    () => commissionCurrencies(payouts, payoutCountry),
    [payouts, payoutCountry],
  );

  // KPI derivations
  const { commissionPeriod, commissionLifetime, sessionsPeriod } = useMemo(() => {
    const range = periodToFromTo(period);
    const fromTs = range.from ? new Date(range.from).getTime() : 0;
    const toTs = range.to ? new Date(range.to).getTime() : Infinity;
    // Skip pending-only states for the realised-commission tiles
    // since they overstate what has actually settled. The lifetime
    // tile counts all non-pending too for the same reason.
    // `payouts`, NOT `allPayouts`. These tiles sat directly above a
    // chart built from the location-filtered rows, so picking one
    // location dropped the chart to that site while every tile kept
    // showing the network total , two numbers about different things,
    // stacked, with nothing saying so. Now the whole card describes one
    // slice: pick a location and the tiles mean "at this location".
    const settled = (p: CommissionRow) => !PENDING_STATUSES.has(p.status);
    const inPeriod = (p: CommissionRow) => {
      const ts = new Date(p.created_at).getTime();
      return ts >= fromTs && ts <= toTs;
    };
    const periodTotals = commissionTotals(payouts, (p) => settled(p) && inPeriod(p));
    return {
      commissionPeriod: periodTotals,
      commissionLifetime: commissionTotals(payouts, settled),
      sessionsPeriod: periodTotals.reduce((n, t) => n + t.count, 0),
    };
  }, [payouts, period]);

  // Chart buckets, one chart per currency. Same shape RevenueChart consumes.
  const charts = useMemo(() => {
    if (period === '24h') {
      const hourEnd = Date.now();
      return commissionCharts(payouts, currencies, 24, (p) =>
        23 - Math.floor((hourEnd - new Date(p.created_at).getTime()) / (60 * 60 * 1000)));
    }
    const days = period === '7d' ? 7 : 30;
    const now = new Date();
    const todayEnd = new Date(
      now.getFullYear(), now.getMonth(), now.getDate(),
      23, 59, 59, 999,
    ).getTime();
    return commissionCharts(payouts, currencies, days, (p) =>
      days - 1 - Math.floor((todayEnd - new Date(p.created_at).getTime()) / MS_PER_DAY));
  }, [payouts, period, currencies]);

  if (!supportedPeriod) {
    return (
      <div className={styles.notice}>
        <p>This report covers up to the last 30 days.</p>
        <p className={styles.noticeSub}>Pick 24h, 7d or 30d above.</p>
      </div>
    );
  }

  const apiPeriod = periodForApi(period);

  return (
    <div className={styles.tabBody}>
      <div className={styles.kpiGrid}>
        <KpiCard
          label="Commission this period"
          value={formatCommissionTotals(commissionPeriod, currencies)}
          meta={describePeriod(period)}
        />
        {/* "Lifetime paid", the settled figure. My Installs shows
            "Lifetime earned" one leaf away, which INCLUDES pending and
            is therefore the larger of the two. A partner should be able
            to see both and know at a glance which is which, so neither
            page prints a bare "lifetime". */}
        <KpiCard
          label="Lifetime paid"
          value={formatCommissionTotals(commissionLifetime, currencies)}
          meta="Settled only, excludes pending"
        />
        <KpiCard
          label="Sessions"
          value={sessionsPeriod.toLocaleString()}
          meta={describePeriod(period)}
        />
        <KpiCard
          label="Avg per session"
          value={formatCommissionAverage(commissionPeriod)}
          meta={describePeriod(period)}
        />
      </div>

      {charts.map((chart) => {
        const footer = commissionChartFooter(chart, commissionPeriod);
        return (
          <div key={chart.key} className={styles.chartCard}>
            <RevenueChart
              data={chart.data}
              period={apiPeriod}
              onPeriodChange={() => {
                // The page-level period picker drives both the totals and
                // this chart, so the chart shows no chips of its own.
              }}
              showPeriodChips={false}
              currentRate={0}
              showRate={false}
              titleLabel={chart.title}
              emptyNoun="commission"
              formatY={commissionAxis(chart)}
              footer={chart.currencies.length > 0 ? (
                <RevenuePeriodPill
                  label="TOTAL"
                  amount={footer.amount}
                  formatAmount={() => footer.label}
                  sessionCount={footer.count}
                />
              ) : undefined}
            />
          </div>
        );
      })}
    </div>
  );
}

// ── Sessions tab body ──────────────────────────────────────────────
//
// Server already SP-scopes /sessions via the Phase 7g.4a sales_agent
// branch in dashboardScope, so fetchSessions returns sessions on the
// SP's assigned chargers without any client-side filter. Same with
// the CSV export endpoint.

const PAGE_SIZE = 25;

function SessionsTab({
  period, locationId, locations,
}: {
  period: ReportPeriod;
  locationId: string | null;
  locations: Location[];
}) {
  const supportedPeriod = period === '24h' || period === '7d' || period === '30d';
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [period, locationId]);

  const range = periodToFromTo(period);
  const sessionsQ = useQuery({
    queryKey: ['sp-reports', 'sessions', period, locationId, page],
    queryFn: () => fetchSessions({
      page,
      limit: PAGE_SIZE,
      ...(locationId ? { location_id: locationId } : {}),
      ...(range.from ? { from: range.from } : {}),
      ...(range.to ? { to: range.to } : {}),
    }),
    enabled: supportedPeriod,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  // CSV download routes through the authenticated ky client via
  // downloadAuthenticatedCsv (admin-export-auth-cleanup). The
  // previous csvHref pattern 401'd because top-level <a download>
  // navigation can't attach a JS-managed Bearer token. Local
  // csvBusy state drives the ExportActions loading indicator.
  // A failed export says why (29 Oct 2026), instead of failing silently.
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const handleDownloadCsv = useCallback(async () => {
    if (csvBusy) return;
    setCsvBusy(true);
    setCsvError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await downloadAuthenticatedCsv({
        path: 'sessions/export',
        query: {
          ...(locationId ? { location_id: locationId } : {}),
          ...periodToFromTo(period),
        },
        fallbackFilename: `evi-sessions-${today}.csv`,
      });
    } catch (err) {
      setCsvError(await downloadErrorMessage(err));
    } finally {
      setCsvBusy(false);
    }
  }, [csvBusy, locationId, period]);

  if (!supportedPeriod) {
    return (
      <div className={styles.notice}>
        <p>This report covers up to the last 30 days.</p>
        <p className={styles.noticeSub}>Pick 24h, 7d or 30d above.</p>
      </div>
    );
  }

  const sessions = sessionsQ.data?.data ?? [];
  const total = sessionsQ.data?.meta.total ?? 0;
  const totalPages = sessionsQ.data?.meta.pages ?? 1;

  const completed = sessions.filter((s) =>
    s.status === 'completed' || s.status === 'stopped_by_user' || s.status === 'stopped_by_system'
  ).length;
  const failed = sessions.filter((s) => s.status === 'failed').length;
  const avgDurationMin = sessions.length > 0
    ? sessions.reduce((acc, s) => acc + (s.duration_minutes ?? 0), 0) / sessions.length
    : 0;
  const avgCostCents = sessions.length > 0
    ? sessions.reduce((acc, s) => acc + (s.cost?.total ?? 0), 0) / sessions.length
    : 0;

  const locationNameById = new Map(locations.map((l) => [l._id, l.name] as const));

  return (
    <div className={styles.tabBody}>
      <div className={styles.kpiGrid}>
        <KpiCard label="Total sessions" value={total.toLocaleString()} meta={describePeriod(period)} />
        <KpiCard
          label="Completed / failed"
          value={`${completed} · ${failed}`}
          meta="this page"
        />
        <KpiCard label="Avg duration" value={`${avgDurationMin.toFixed(0)}m`} meta="this page" />
        <KpiCard label="Avg cost" value={formatCents(avgCostCents, 'USD')} meta="this page" />
      </div>

      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <h3 className={styles.tableTitle}>Sessions</h3>
          <div className={styles.tableHeaderRight}>
            <span className={styles.tableMeta}>
              {total > 0 ? `${total.toLocaleString()} total` : ''}
            </span>
            <ExportActions onCsv={handleDownloadCsv} csvBusy={csvBusy} pdfHref={null} />
          </div>
        </div>
        {csvError && <div className={styles.errorBanner} role="alert">{csvError}</div>}
        <SessionsTable
          sessions={sessions}
          locationNameById={locationNameById}
          loading={sessionsQ.isLoading}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}

const STATUS_LABELS: Record<Session['status'], string> = {
  active: 'Active',
  completed: 'Completed',
  failed: 'Failed',
  stopped_by_user: 'Stopped (user)',
  stopped_by_system: 'Stopped (system)',
};

function SessionsTable({
  sessions, locationNameById, loading, page, totalPages, onPageChange,
}: {
  sessions: Session[];
  locationNameById: Map<string, string>;
  loading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (next: number) => void;
}) {
  if (loading && sessions.length === 0) {
    return <p className={styles.emptyState}>Loading…</p>;
  }
  if (sessions.length === 0) {
    return <p className={styles.emptyState}>No sessions for the selected filter.</p>;
  }
  return (
    <>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Started</th>
            <th>Status</th>
            <th>Location</th>
            <th className={styles.numCell}>Duration</th>
            <th className={styles.numCell}>Energy</th>
            <th className={styles.numCell}>Cost</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => (
            <tr key={s._id}>
              <td>
                <span className={styles.cellMeta}>
                  {new Date(s.started_at).toLocaleString(undefined, {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}
                </span>
              </td>
              <td>
                <span className={`${styles.statusBadge} ${
                  s.status === 'completed' ? styles.statusOk
                    : s.status === 'failed' ? styles.statusBad
                      : styles.statusNeutral}`}
                >
                  {STATUS_LABELS[s.status]}
                </span>
              </td>
              <td className={styles.cellMeta}>
                {locationNameById.get(s.location_id) ?? 'Not available'}
              </td>
              <td className={styles.numCell}>
                {s.duration_minutes ? `${Math.round(s.duration_minutes)}m` : 'Not available'}
              </td>
              <td className={styles.numCell}>{s.energy_kwh.toFixed(1)} kWh</td>
              <td className={styles.numCell}>
                {formatCents(s.cost?.total ?? 0, s.cost?.currency ?? 'USD')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className={styles.pager}>
          <span className={styles.pagerMeta}>Page {page} of {totalPages}</span>
          <div className={styles.pagerButtons}>
            <button
              type="button"
              className={styles.pagerBtn}
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
            >Previous</button>
            <button
              type="button"
              className={styles.pagerBtn}
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
            >Next</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Energy tab body ────────────────────────────────────────────────
//
// operator-summary's energyLast24h series, SP-scoped via the
// sales_agent branch in dashboardScope. Same RevenueChart component
// the operator Energy tab uses, swapped to energy units via the
// titleLabel + formatY props.

const CO2_COEFFICIENT_KG_PER_KWH = 0.4;

function EnergyTab({
  period, locationId, onPeriodChange,
}: {
  period: ReportPeriod;
  locationId: string | null;
  onPeriodChange: (next: ReportPeriod) => void;
}) {
  const supportedPeriod = period === '24h' || period === '7d' || period === '30d';
  const apiPeriod = periodForApi(period);
  const locationIds = locationId ? [locationId] : [];

  const summaryQ = useQuery({
    queryKey: ['sp-reports', 'energy', 'summary', apiPeriod, locationIds],
    queryFn: () => fetchOperatorSummary(apiPeriod, { locationIds }),
    enabled: supportedPeriod,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  if (!supportedPeriod) {
    return (
      <div className={styles.notice}>
        <p>This report covers up to the last 30 days.</p>
        <p className={styles.noticeSub}>Pick 24h, 7d or 30d above.</p>
      </div>
    );
  }

  const summary = summaryQ.data;
  const energyBuckets = summary?.energyLast24h ?? [];
  const totalKwh = energyBuckets.reduce((acc, v) => acc + v, 0);
  const co2Kg = totalKwh * CO2_COEFFICIENT_KG_PER_KWH;
  const networkSize = summary?.network.total ?? 0;
  const days = period === '24h' ? 1 : period === '7d' ? 7 : 30;
  const energyPerChargerPerDay = networkSize > 0 ? totalKwh / networkSize / days : 0;

  return (
    <div className={styles.tabBody}>
      <div className={styles.kpiGrid}>
        <KpiCard
          label="Total energy"
          value={totalKwh.toFixed(1)}
          unit="kWh"
          meta={describePeriod(period)}
        />
        <KpiCard
          label="Chargers in scope"
          value={networkSize.toLocaleString()}
          meta="assigned to you"
        />
        <KpiCard
          label="Estimated CO₂ offset"
          value={co2Kg.toFixed(0)}
          unit="kg"
          meta="0.4 kg / kWh · NA grid avg"
        />
        <KpiCard
          label="Energy / charger / day"
          value={energyPerChargerPerDay.toFixed(1)}
          unit="kWh"
          meta={describePeriod(period)}
        />
      </div>

      <div className={styles.chartCard}>
        <RevenueChart
          data={energyBuckets}
          period={apiPeriod}
          onPeriodChange={onPeriodChange}
          allowedPeriods={['24h', '7d', '30d']}
          titleLabel="ENERGY: kWh"
          emptyNoun="energy"
          formatY={(n) => `${Math.round(n)} kWh`}
          currentRate={0}
          showRate={false}
          footer={
            <RevenuePeriodPill
              label="TOTAL"
              amount={totalKwh}
              sessionCount={0}
            />
          }
        />
      </div>
    </div>
  );
}

// ── Utilization tab body ───────────────────────────────────────────
//
// Three SP-scoped endpoints: charger-utilization, charger-uptime,
// session-trends. All three honor the sales_agent branch server-
// side so the data shown is naturally SP-scoped. Period chips are
// informational here since each endpoint has its own fixed window
// (utilization is 7d, uptime is 24h, trends is 30d). Same constraint
// the operator Utilization tab carries.

function UtilizationTab({
  locationId,
}: { locationId: string | null }) {
  const utilQ = useQuery({
    queryKey: ['sp-reports', 'utilization', 'rows'],
    queryFn: () => fetchChargerUtilization(),
    staleTime: 60_000,
  });
  const uptimeQ = useQuery({
    queryKey: ['sp-reports', 'utilization', 'uptime'],
    queryFn: () => fetchChargerUptime(),
    staleTime: 60_000,
  });
  const trendsQ = useQuery({
    queryKey: ['sp-reports', 'utilization', 'trends'],
    queryFn: () => fetchSessionTrends(),
    staleTime: 60_000,
  });

  const allRows = utilQ.data?.rows ?? [];
  // Location filter is a no-op on /dashboard/charger-utilization
  // today (no server-side filter, no location_id on rows). Same
  // limitation the operator Utilization tab carries; flagged for
  // future server-side extension.
  const utilRows = locationId
    ? allRows.filter((r) => r.location_name)
    : allRows;

  const totalSessions = utilRows.reduce((acc, r) => acc + r.session_count, 0);
  const chargerCount = utilRows.length;
  const avgSessionsPerChargerPerDay = chargerCount > 0
    ? totalSessions / chargerCount / 7
    : 0;
  const networkUptime = uptimeQ.data?.percent_online_now ?? 0;
  const avgDuration = trendsQ.data?.avg_duration_minutes_7d ?? null;
  const avgKwh = trendsQ.data?.avg_kwh_7d ?? null;

  const throughputData = (trendsQ.data?.daily_buckets ?? []).map((b) => ({
    sessions: b.session_count,
    kwh: b.avg_kwh != null ? b.session_count * b.avg_kwh : 0,
  }));

  return (
    <div className={styles.tabBody}>
      <div className={styles.kpiGrid}>
        <KpiCard
          label="Avg sessions / charger / day"
          value={avgSessionsPerChargerPerDay.toFixed(2)}
          meta="last 7 days"
        />
        <KpiCard
          label="Network uptime"
          value={`${networkUptime.toFixed(1)}%`}
          meta={uptimeQ.data ? `${uptimeQ.data.total_chargers} chargers in scope` : 'now'}
        />
        <KpiCard
          label="Avg session duration"
          value={avgDuration != null ? `${avgDuration.toFixed(0)}m` : 'Not available'}
          meta="last 7 days"
        />
        <KpiCard
          label="Avg kWh / session"
          value={avgKwh != null ? avgKwh.toFixed(1) : 'Not available'}
          unit="kWh"
          meta="last 7 days"
        />
      </div>

      <div className={styles.chartCard}>
        <ThroughputChart
          data={throughputData}
          period="30d"
          onPeriodChange={() => { /* fixed 30d; period chip taps are no-ops */ }}
          allowedPeriods={['30d']}
        />
      </div>

      <UtilizationByChargerTable rows={utilRows} loading={utilQ.isLoading} />
    </div>
  );
}

type UtilRow = {
  charger_id: string;
  charger_identity: string;
  location_name: string | null;
  utilization_percent: number;
  session_count: number;
  total_active_minutes: number;
};

function UtilizationByChargerTable({
  rows, loading,
}: { rows: UtilRow[]; loading: boolean }) {
  const sorted = useMemo(() => {
    return [...rows]
      .sort((a, b) => b.utilization_percent - a.utilization_percent)
      .slice(0, 10);
  }, [rows]);

  return (
    <div className={styles.tableCard}>
      <h3 className={styles.tableTitle}>Top chargers by utilization</h3>
      {loading && rows.length === 0 ? (
        <p className={styles.emptyState}>Loading…</p>
      ) : sorted.length === 0 ? (
        <p className={styles.emptyState}>No utilization data yet.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Charger</th>
              <th>Location</th>
              <th className={styles.numCell}>Sessions</th>
              <th className={styles.numCell}>Utilization %</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.charger_id}>
                <td><code className={styles.codeCell}>{r.charger_identity}</code></td>
                <td>{r.location_name ?? 'Not available'}</td>
                <td className={styles.numCell}>{r.session_count.toLocaleString()}</td>
                <td className={styles.numCell}>{r.utilization_percent.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Payouts tab body ───────────────────────────────────────────────
//
// Composition:
//   1. Stripe Connect status strip , pill + Open Stripe Dashboard
//      button. The strip lives at the top of the Payouts tab body
//      rather than as its own tab so the SP sees their settlement
//      destination context immediately when they pull this surface
//      up for a money question.
//   2. AgentPayouts table , full commission ledger with Date,
//      Session, Charger, Location, Commission, Status. Same column
//      shape the Dashboard ledger uses post-Commit 2.
//   3. CSV export , ExportActions calling downloadAuthenticatedCsv.
//      The new admin/agents/:id/payouts/export.csv endpoint backs
//      this; row cap 10000, columns mirror the table.

function PayoutsTab({
  agentId, agent,
}: {
  agentId: string;
  agent: AgentRow | null;
}) {
  const payoutsQ = useQuery({
    queryKey: ['sp-reports', 'payouts-tab', agentId],
    queryFn: () => listAgentPayouts(agentId),
    staleTime: 30_000,
  });
  const payouts = payoutsQ.data?.rows ?? [];

  // CSV download , routes through the shared downloadAuthenticatedCsv
  // helper. Previously this tab inlined the entire blob-fetch +
  // synthesized-anchor pattern; the helper extraction
  // (admin-export-auth-cleanup) lifted it to a single module so
  // operator + SP Sessions + this tab share one implementation.
  // Behaviour identical to the prior inline version , same
  // Content-Disposition filename extraction with the same fallback
  // shape, same local csvBusy state for the loading indicator.
  // A failed export says why (29 Oct 2026), instead of failing silently.
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const handleDownloadCsv = useCallback(async () => {
    if (csvBusy) return;
    setCsvBusy(true);
    setCsvError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await downloadAuthenticatedCsv({
        path: `admin/agents/${agentId}/payouts/export.csv`,
        fallbackFilename: `evi-commissions-${agentId.slice(-8)}-${today}.csv`,
      });
    } catch (err) {
      setCsvError(await downloadErrorMessage(err));
    } finally {
      setCsvBusy(false);
    }
  }, [agentId, csvBusy]);

  return (
    <div className={styles.tabBody}>
      <StripeConnectStatusStrip agentId={agentId} agent={agent} />

      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <h3 className={styles.tableTitle}>Commission history</h3>
          <div className={styles.tableHeaderRight}>
            <span className={styles.tableMeta}>
              {payouts.length > 0 ? `${payouts.length.toLocaleString()} ${payouts.length === 1 ? 'commission' : 'commissions'}` : ''}
            </span>
            <ExportActions onCsv={handleDownloadCsv} csvBusy={csvBusy} pdfHref={null} />
          </div>
        </div>
        {csvError && <div className={styles.errorBanner} role="alert">{csvError}</div>}
        <PayoutsTable payouts={payouts} loading={payoutsQ.isLoading} />
      </div>
    </div>
  );
}

// ── Stripe Connect status strip ────────────────────────────────────
//
// Current Stripe readiness drives both the pill and the action.
function StripeConnectStatusStrip({
  agentId, agent,
}: { agentId: string; agent: AgentRow | null }) {
  const [openBusy, setOpenBusy] = useState(false);
  const [openErr, setOpenErr] = useState('');

  const hasConnect = !!agent?.stripe_connect_account_id;
  const status = usePartnerConnectStatus(agentId);
  const presentation = partnerConnectPresentation(status.data, hasConnect, payoutLocale());
  const text = payoutSetupCopy(payoutLocale());

  const handleOpenDashboard = async (): Promise<void> => {
    if (!hasConnect || openBusy) return;
    setOpenErr('');
    setOpenBusy(true);
    try {
      const { url } = presentation.dashboard ? await fetchAgentDashboardLink(agentId) : await startAgentConnect(agentId);
      window.location.href = url;
    } catch (err) {
      setOpenBusy(false);
      setOpenErr(await getDisplayErrorMessage(err, text.spOpenDashboardFailed));
    }
  };

  return (
    <div className={styles.connectStrip}>
      <div className={styles.connectStripLeft}>
        <span className={`${styles.connectPill} ${
          presentation.ready ? styles.connectPillOk : styles.connectPillNeutral
        }`}>
          {presentation.ready ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
          {presentation.pill}
        </span>
        {/* No "from the banner above" hint: before money is owed there is no banner (SPEC_PAYOUT_SETUP_WHEN_MONEY). */}
        {openErr && <span className={styles.connectError}>{openErr}</span>}
      </div>
      <div className={styles.connectStripRight}>
        {hasConnect && (
          <button
            type="button"
            className={styles.connectBtn}
            onClick={() => void handleOpenDashboard()}
            disabled={openBusy}
          >
            {openBusy ? text.spOpening : presentation.dashboard ? text.spOpenDashboard : text.spButtonFinish}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Payouts table (the ledger) ─────────────────────────────────────

function PayoutsTable({
  payouts, loading,
}: { payouts: CommissionRow[]; loading: boolean }) {
  if (loading && payouts.length === 0) {
    return <p className={styles.emptyState}>Loading…</p>;
  }
  if (payouts.length === 0) {
    return (
      <p className={styles.emptyState}>
        Your commission history will appear here once sessions run on your assigned chargers.
      </p>
    );
  }
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Date</th>
          <th>Charger</th>
          <th>Location</th>
          <th className={styles.numCell}>Commission</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {payouts.map((p) => (
          <tr key={p.id}>
            <td className={styles.cellMeta}>
              {new Date(p.created_at).toLocaleString(undefined, {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            </td>
            {/* No session id column: an id means nothing to a partner (Warren, 28 October 2026: no developer copy).
                The CSV export still carries it for anyone reconciling. */}
            <td>
              {p.charger_identity
                ? <code className={styles.codeCell}>{p.charger_identity}</code>
                : <span className={styles.cellMuted}>Not available</span>}
            </td>
            <td>{p.location_name ?? <span className={styles.cellMuted}>Not available</span>}</td>
            <td className={styles.numCell}>
              {formatCommissionRow(p)}
            </td>
            <td><PayoutStatusPill status={p.status} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PayoutStatusPill({ status }: { status: string }) {
  const fallback = styles.pillMuted ?? '';
  const tones: Record<PayoutStatusTone, string> = {
    ok: styles.pillOk ?? fallback, info: styles.pillInfo ?? fallback, warn: styles.pillWarn ?? fallback, muted: fallback,
  };
  // Words for every status, never the raw key (lib/agentPayoutStatus.ts).
  const c = agentPayoutStatusLabel(status);
  return <span className={`${styles.pill} ${tones[c.tone]}`}>{c.label}</span>;
}
